import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  texto,
  numero,
  moneda,
  fecha,
  totalizar,
  construirHojaTabla,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
} from "../../../../Components/ExcelTablaReporte";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Excel y PDF del Reporte Ventas Por Lote.
 *
 * Una sola tabla, armada una vez, alimenta los dos archivos para que no puedan decir cosas
 * distintas (mismo patron que `ReporteCostoVsUltimaCompra/exportarCostoVsUltimaCompra.js`).
 */

const COLUMNAS = [
  { titulo: "Folio", tipo: "texto", ancho: 16, anchoPdf: 62 },
  { titulo: "Fecha", tipo: "fecha", ancho: 12, anchoPdf: 52 },
  { titulo: "Cliente", tipo: "texto", ancho: 34, anchoPdf: 118 },
  { titulo: "Codigo", tipo: "texto", ancho: 14, enPdf: false },
  { titulo: "Clave", tipo: "texto", ancho: 14, anchoPdf: 54 },
  { titulo: "Descripcion", tipo: "texto", ancho: 40, anchoPdf: 130 },
  { titulo: "Lote", tipo: "texto", ancho: 18, anchoPdf: 66 },
  { titulo: "Caducidad", tipo: "fecha", ancho: 12, anchoPdf: 52 },
  { titulo: "Cantidad", tipo: "numero", ancho: 11, anchoPdf: 48 },
  // La cantidad tal como se descargo del lote, en unidades de INVENTARIO. Difiere de "Cantidad"
  // cuando el renglon apunta a una equivalencia, asi que fuera del PDF para no confundir.
  { titulo: "Cant. Inventario", tipo: "numero", ancho: 15, enPdf: false },
  { titulo: "P.U.", tipo: "moneda", ancho: 14, anchoPdf: 58 },
  { titulo: "Importe", tipo: "moneda", ancho: 16, anchoPdf: 66 },
  { titulo: "Moneda", tipo: "texto", ancho: 9, enPdf: false },
  { titulo: "Estado", tipo: "texto", ancho: 12, enPdf: false },
];

// Un renglon de venta cancelada se resalta: sigue apareciendo (el filtro de estado lo decide el
// usuario) pero no debe leerse como una entrega vigente. Son dos mecanismos distintos y hacen
// falta los dos: `fillColor` lo pinta el PDF y el Excel lo ignora; `resalte` es al reves, y
// ademas conserva el numFmt de Contabilidad en las celdas de dinero.
const RELLENO_CANCELADA = "#FFCCC7";

export const construirTabla = (filas) => {
  const cuerpo = (filas || []).map((fila) => {
    const celdas = [
      texto(fila.folio),
      fecha(fila.fechaOrden),
      texto(fila.cliente),
      texto(fila.codigo),
      texto(fila.clave),
      texto(fila.descripcion),
      texto(fila.lote),
      fecha(fila.caducidadOrden),
      numero(fila.cantidad),
      numero(fila.cantidadInventario),
      moneda(fila.precioUnitario),
      moneda(fila.importe),
      texto(fila.moneda),
      texto(fila.cancelada ? "Cancelada" : "Vigente"),
    ];

    return fila.cancelada
      ? celdas.map((celda) => ({ ...celda, fillColor: RELLENO_CANCELADA, resalte: "nota" }))
      : celdas;
  });

  // Los totales se arman a mano y NO con `totalizar`: ese suma toda columna de tipo moneda, y
  // sumar "P.U." daria la suma de precios unitarios de insumos distintos, que no significa nada.
  // Solo se totaliza el Importe, que es lo unico aditivo de la tabla.
  const indiceImporte = COLUMNAS.findIndex((columna) => columna.titulo === "Importe");
  const totales = COLUMNAS.map(() => null);
  totales[indiceImporte] = totalizar(COLUMNAS, cuerpo)[indiceImporte];

  return { columnas: COLUMNAS, filas: cuerpo, totales };
};

const TITULO = "Insumos con lote entregados por venta";

const nombreArchivo = (empresaNombre) =>
  `Ventas_Por_Lote_${limpiarNombreArchivo(empresaNombre)}_${moment().format("YYYY-MM-DD")}`;

const indicadoresDe = (totales = {}) => [
  { etiqueta: "Renglones", valor: String(totales.filas || 0) },
  { etiqueta: "Ventas", valor: String(totales.ventas || 0) },
  { etiqueta: "Lotes distintos", valor: String(totales.lotesDistintos || 0) },
  {
    etiqueta: "Importe",
    valor: `$${(totales.importe || 0).toLocaleString("es-MX", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`,
  },
];

export const descargarExcel = ({ datos, empresaNombre, descripcionFiltros }) => {
  descargarLibro(
    [
      {
        nombre: "Ventas Por Lote",
        hoja: construirHojaTabla(construirTabla(datos), {
          titulo: TITULO,
          empresaNombre,
          descripcionFiltros,
        }),
      },
    ],
    nombreArchivo(empresaNombre)
  );
};

export const descargarPdf = ({ datos, totales, empresaNombre, descripcionFiltros }) => {
  const tabla = construirTabla(datos);

  pdfMake
    .createPdf(
      reporteFiscalPdf({
        titulo: "Reporte Ventas Por Lote",
        empresaNombre,
        descripcionFiltros,
        indicadores: indicadoresDe(totales),
        contador: tabla.filas.length,
        secciones: [{ titulo: TITULO, ...paraPdf(tabla) }],
      })
    )
    .download(`${nombreArchivo(empresaNombre)}.pdf`);
};
